import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useFavorites, type FavoriteProperty } from '../../../hooks/useFavorites';
import { usePropertyStore } from '../../../hooks/usePropertyStore';
import { useToast } from '../../../components/common/ToastProvider';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import { apiRequest } from '../../../utils/apiClient';
import { formatAmount } from '../../../utils/numberFormat';
import { GooeyLoader } from '../../../components/ui/gooey-loader';

interface PropertyDetails {
  id: string;
  propertyId: string;
  title: string;
  city: string;
  state: string;
  price: number;
  imageUrl: string | null;
  createdAt: string;
}

export default function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['favorites', 'common']);
  const { user, session } = useAuth();
  const { showToast } = useToast();
  const {
    getListById,
    getFavoritesInList,
    removeFavorite,
    defaultListId: _defaultListId
  } = useFavorites();
  const { getPropertyById: getLocalPropertyById } = usePropertyStore();

  const isRTL = i18n.dir() === 'rtl';

  // View mode state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Properties state
  const [properties, setProperties] = useState<PropertyDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingBulk, setRemovingBulk] = useState(false);

  // Get list info
  const list = listId ? getListById(listId) : undefined;
  const listFavorites = listId ? getFavoritesInList(listId) : [];

  // Redirect if not logged in or list not found
  useEffect(() => {
    if (!user) {
      navigate('/signin', { state: { from: { pathname: `/favorites/${listId}` } } });
    } else if (listId && !list && !loading) {
      navigate('/favorites');
    }
  }, [user, listId, list, navigate, loading]);

  // Fetch property details
  const fetchProperties = useCallback(async () => {
    if (!listId || !user) return;

    const favorites = getFavoritesInList(listId);
    if (favorites.length === 0) {
      setProperties([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const propertyIds = new Set(favorites.map((f: FavoriteProperty) => f.propertyId));
      const propertiesWithDetails: PropertyDetails[] = [];
      const foundPropertyIds = new Set<string>();

      // Try to get properties from backend first
      if (session?.token) {
        try {
          const response = await apiRequest<{
            data: Array<{
              id: string;
              propertyId: string;
              createdAt: string;
              property?: {
                id: string;
                title: string;
                titleAr?: string;
                price: number;
                images?: Array<{ url: string }>;
                city?: { nameEn: string; nameAr: string };
                area?: { nameEn: string; nameAr: string };
              };
            }>;
          }>('/favorites?page=1&pageSize=100', { token: session.token });

          const favoritesData = response.data || [];

          for (const fav of favoritesData) {
            if (fav.property && propertyIds.has(fav.propertyId)) {
              const prop = fav.property;
              const cityName = i18n.language === 'ar' ? prop.city?.nameAr : prop.city?.nameEn;
              const areaName = i18n.language === 'ar' ? prop.area?.nameAr : prop.area?.nameEn;
              const imageUrl = prop.images && prop.images.length > 0 ? prop.images[0].url : null;

              propertiesWithDetails.push({
                id: fav.id,
                propertyId: fav.propertyId,
                title: i18n.language === 'ar' && prop.titleAr ? prop.titleAr : prop.title || '',
                city: cityName || '',
                state: areaName || '',
                price: prop.price || 0,
                imageUrl,
                createdAt: fav.createdAt
              });
              foundPropertyIds.add(fav.propertyId);
            }
          }
        } catch (apiError) {
          console.warn('Failed to fetch from backend, using local fallback:', apiError);
        }
      }

      // Fallback: any list member NOT returned by /favorites (e.g. saved to the
      // list but its separate favorite record is missing) — resolve details from
      // the local store first, then by fetching the property directly. This
      // guarantees the list shows every member, matching its count.
      const missing = favorites.filter((f: FavoriteProperty) => !foundPropertyIds.has(f.propertyId));
      await Promise.all(
        missing.map(async (fav: FavoriteProperty) => {
          const localProperty = getLocalPropertyById(fav.propertyId);
          if (localProperty) {
            propertiesWithDetails.push({
              id: fav.id || fav.propertyId,
              propertyId: fav.propertyId,
              title: localProperty.title || '',
              city: localProperty.city || '',
              state: localProperty.state || '',
              price: localProperty.price || 0,
              imageUrl: localProperty.imageUrl || null,
              createdAt: fav.createdAt || new Date().toISOString(),
            });
            foundPropertyIds.add(fav.propertyId);
            return;
          }
          try {
            const resp = await apiRequest<{
              data: {
                title?: string; titleAr?: string;
                city?: string; cityAr?: string;
                area?: string; areaAr?: string; state?: string; stateAr?: string;
                price?: number; imageUrl?: string; galleryImages?: string[]; imageUrls?: string[];
              };
            }>(`/properties/${fav.propertyId}`);
            const p = resp.data;
            if (p) {
              propertiesWithDetails.push({
                id: fav.id || fav.propertyId,
                propertyId: fav.propertyId,
                title: (i18n.language === 'ar' && p.titleAr ? p.titleAr : p.title) || '',
                city: (i18n.language === 'ar' ? p.cityAr || p.city : p.city) || '',
                state: (i18n.language === 'ar' ? p.areaAr || p.area || p.stateAr || p.state : p.area || p.state) || '',
                price: p.price || 0,
                imageUrl: p.imageUrl || (p.galleryImages && p.galleryImages[0]) || (p.imageUrls && p.imageUrls[0]) || null,
                createdAt: fav.createdAt || new Date().toISOString(),
              });
              foundPropertyIds.add(fav.propertyId);
            }
          } catch {
            // Property unavailable (deleted/unpublished) — skip it.
          }
        })
      );

      // Sort by createdAt (newest first)
      propertiesWithDetails.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setProperties(propertiesWithDetails);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [listId, user, session?.token, i18n.language, getFavoritesInList, getLocalPropertyById]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties, listFavorites.length]);

  // Selection handlers
  const toggleSelection = (propertyId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(properties.map(p => p.propertyId)));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;

    setRemovingBulk(true);
    try {
      const count = selectedIds.size;
      for (const propertyId of selectedIds) {
        await removeFavorite(propertyId, listId);
      }
      showToast(t('favorites.toast.bulkRemoved', { count }), 'success');
      setSelectedIds(new Set());
      setSelectionMode(false);
    } catch (error) {
      console.error('Failed to remove favorites:', error);
      showToast(t('common:error'), 'error');
    } finally {
      setRemovingBulk(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />

      <main className="pb-20">
        {/* Header */}
        <section className="bg-[#004D40] py-8">
          <div className="max-w-6xl mx-auto px-4">
            {/* Back button */}
            <Link
              to="/favorites"
              className={`inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <i className={`${isRTL ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} text-lg`}></i>
              <span>{t('common:back')}</span>
            </Link>

            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: list?.color ? `${list.color}30` : 'rgba(255,255,255,0.2)' }}
                >
                  <i
                    className={`${list?.icon || 'ri-heart-line'} text-2xl`}
                    style={{ color: list?.color || 'white' }}
                  ></i>
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h1 className="text-2xl font-bold text-white mb-1">
                    {list?.name || t('favorites.lists.defaultName')}
                  </h1>
                  <p className="text-purple-100">
                    {t('favorites.lists.propertyCount', { count: properties.length })}
                  </p>
                </div>
              </div>

              {/* View toggle and Select button */}
              {!selectionMode && properties.length > 0 && (
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {/* View toggle */}
                  <div className="flex bg-white/20 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'grid' ? 'bg-white text-[#004D40]' : 'text-white hover:bg-white/10'
                      }`}
                      title={t('favorites.viewModes.grid')}
                    >
                      <i className="ri-grid-fill text-lg"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'list' ? 'bg-white text-[#004D40]' : 'text-white hover:bg-white/10'
                      }`}
                      title={t('favorites.viewModes.list')}
                    >
                      <i className="ri-list-unordered text-lg"></i>
                    </button>
                  </div>

                  {/* Select button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectionMode(true)}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <i className={`ri-checkbox-multiple-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    {t('favorites.selection.selectMode')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Selection Mode Toolbar */}
        {selectionMode && (
          <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
            <div className="max-w-6xl mx-auto px-4 py-3">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-sm font-medium text-gray-700">
                    {t('favorites.selection.selected', { count: selectedIds.size })}
                  </span>
                  <button
                    onClick={selectedIds.size === properties.length ? handleDeselectAll : handleSelectAll}
                    className="text-sm text-[#004D40] hover:text-purple-700 font-medium"
                  >
                    {selectedIds.size === properties.length
                      ? t('favorites.selection.deselectAll')
                      : t('favorites.selection.selectAll')}
                  </button>
                </div>

                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSelection}
                    disabled={removingBulk}
                  >
                    {t('favorites.selection.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleBulkRemove}
                    disabled={selectedIds.size === 0 || removingBulk}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    {removingBulk ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <>
                        <i className={`ri-delete-bin-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                        {t('favorites.selection.removeSelected')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
                <p className="text-gray-500">{t('favorites.loading')}</p>
              </div>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
                <i className={`${list?.icon || 'ri-heart-line'} text-4xl text-[#3C9F8F]`}></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('favorites.empty.listEmpty')}
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {t('favorites.empty.description')}
              </p>
              <Link to="/search">
                <Button variant="primary">
                  <i className={`ri-search-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                  {t('favorites.empty.cta')}
                </Button>
              </Link>
            </div>
          ) : viewMode === 'grid' ? (
            // Grid View
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <div
                  key={property.propertyId}
                  className={`group relative bg-white rounded-2xl border overflow-hidden transition-all ${
                    selectionMode && selectedIds.has(property.propertyId)
                      ? 'border-[#004D40] ring-2 ring-[#004D40]/20'
                      : 'border-gray-200 hover:shadow-lg hover:border-[var(--pv-border-soft)]'
                  }`}
                >
                  {/* Selection Checkbox */}
                  {selectionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(property.propertyId);
                      }}
                      className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} z-10 w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${
                        selectedIds.has(property.propertyId)
                          ? 'bg-[#004D40] border-purple-600 text-white'
                          : 'bg-white border-gray-300 hover:border-[#3C9F8F]'
                      }`}
                    >
                      {selectedIds.has(property.propertyId) && (
                        <i className="ri-check-line text-sm"></i>
                      )}
                    </button>
                  )}

                  {/* Image */}
                  <div className="relative h-48 bg-gray-100">
                    {property.imageUrl ? (
                      <img
                        src={property.imageUrl}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-home-line text-4xl text-gray-300"></i>
                      </div>
                    )}

                    {/* Price Badge */}
                    <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} flex items-center gap-1.5 bg-[#004D40] text-white px-3 py-1.5 rounded-lg text-sm font-semibold`}>
                      <SaudiRiyalSymbol className="w-4 h-4" />
                      {formatAmount(property.price)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h4 className={`font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-[#004D40] transition-colors ${isRTL ? 'text-right' : ''}`}>
                      {property.title}
                    </h4>
                    <p className={`text-sm text-gray-500 mb-4 flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <i className="ri-map-pin-line text-[#004D40]"></i>
                      {property.city}{property.state ? `, ${property.state}` : ''}
                    </p>

                    <Link
                      to={`/property/${property.propertyId}`}
                      className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-medium text-[#004D40] bg-[var(--pv-color-surface-sunken)] hover:bg-[var(--pv-color-surface-sunken)] transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <i className="ri-eye-line"></i>
                      {t('favorites.actions.viewDetails')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div className="space-y-3">
              {properties.map((property) => (
                <div
                  key={property.propertyId}
                  className={`group flex items-center gap-4 bg-white rounded-xl border p-3 transition-all ${
                    selectionMode && selectedIds.has(property.propertyId)
                      ? 'border-[#004D40] ring-2 ring-[#004D40]/20'
                      : 'border-gray-200 hover:shadow-md hover:border-[var(--pv-border-soft)]'
                  } ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  {/* Selection Checkbox */}
                  {selectionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(property.propertyId);
                      }}
                      className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                        selectedIds.has(property.propertyId)
                          ? 'bg-[#004D40] border-purple-600 text-white'
                          : 'bg-white border-gray-300 hover:border-[#3C9F8F]'
                      }`}
                    >
                      {selectedIds.has(property.propertyId) && (
                        <i className="ri-check-line text-xs"></i>
                      )}
                    </button>
                  )}

                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-24 h-20 rounded-lg bg-gray-100 overflow-hidden">
                    {property.imageUrl ? (
                      <img
                        src={property.imageUrl}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-home-line text-2xl text-gray-300"></i>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className={`flex-grow min-w-0 ${isRTL ? 'text-right' : ''}`}>
                    <h4 className="font-semibold text-gray-900 truncate group-hover:text-[#004D40] transition-colors">
                      {property.title}
                    </h4>
                    <p className={`text-sm text-gray-500 flex items-center gap-1.5 mt-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <i className="ri-map-pin-line text-[#004D40]"></i>
                      {property.city}{property.state ? `, ${property.state}` : ''}
                    </p>
                  </div>

                  {/* Price */}
                  <div className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-[var(--pv-color-surface-sunken)] rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <SaudiRiyalSymbol className="w-4 h-4 text-[#004D40]" />
                    <span className="font-semibold text-[#004D40]">
                      {formatAmount(property.price)}
                    </span>
                  </div>

                  {/* View button */}
                  <Link
                    to={`/property/${property.propertyId}`}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] rounded-lg transition-colors"
                    title={t('favorites.actions.viewDetails')}
                  >
                    <i className={`${isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} text-xl`}></i>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
